import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Bot, PhoneForwarded } from 'lucide-react'
import { Button } from '../../ui/Button/Button'
import { Switch } from '../../ui/Switch/Switch'
import { useScopedToast } from '../../ui/Toaster/useScopedToast'
import type { AssistantSettingsRepository } from './assistant-settings-repository'
import { formatPhone } from '../patients/patient-display'
import panel from './SettingsSection.module.css'
import styles from './CallHandlingSettings.module.css'

type Props = {
  tenantId: string
  repository: AssistantSettingsRepository
}

export function CallHandlingSettings(props: Props) {
  return <TenantCallHandling key={props.tenantId} {...props} />
}

function TenantCallHandling({ tenantId, repository }: Props) {
  const aiOff = useAIOff(tenantId, repository)
  return (
    <section
      className={panel.panel}
      aria-labelledby={`call-handling-title-${tenantId}`}
    >
      <header className={panel.panelHeader}>
        <div className={panel.heading}>
          <span className={panel.eyebrow}>Calls</span>
          <h2 id={`call-handling-title-${tenantId}`}>Call Handling</h2>
          <p>
            Ava answers every call that reaches Flio, at any hour, while Voice
            AI is on. These settings cover phone calls only; texting keeps
            working either way.
          </p>
        </div>
      </header>
      <div className={styles.policies}>
        <AIEnabled tenantId={tenantId} repository={repository} />
        <FrontDeskTransfer
          tenantId={tenantId}
          repository={repository}
          aiOff={aiOff}
        />
      </div>
    </section>
  )
}

/**
 * Whether Voice AI is turned off, read from the Voice AI query's cache with the
 * same query function; it never fetches on its own.
 */
function useAIOff(tenantId: string, repository: Props['repository']) {
  const query = useQuery({
    queryKey: ['settings', tenantId, 'voice-ai-control'],
    queryFn: () => repository.getVoiceAIControl(tenantId),
    enabled: false,
  })
  return !query.isError && query.data?.turnOffAI === true
}

/**
 * The clinic's stored control is `turnOffAI`, which governs Voice only: when
 * true, Ava answers no new call and the call is handled by text: Flio says the
 * front desk is unavailable and hangs up, and Missed Call Follow-Up texts the
 * caller. Calls already
 * with Ava finish normally, and no texting depends on it. Staff see it the
 * positive way round: Voice AI is on when `turnOffAI === false`. The write
 * still sends the stored value as its compare-and-set expectation and checks
 * the readback.
 */
function AIEnabled({ tenantId, repository }: Props) {
  const client = useQueryClient()
  const notify = useScopedToast()
  const [saving, setSaving] = useState(false)
  const key = ['settings', tenantId, 'voice-ai-control'] as const
  const query = useQuery({
    queryKey: key,
    queryFn: () => repository.getVoiceAIControl(tenantId),
    retry: false,
    staleTime: 0,
    refetchOnMount: 'always',
  })
  const data = query.isError ? undefined : query.data

  async function update(turnOffAI: boolean) {
    if (!data || saving || query.isFetching) return
    setSaving(true)
    try {
      const result = await repository.updateVoiceAIControl(tenantId, {
        expectedTurnOffAI: data.turnOffAI,
        turnOffAI,
      })
      if (result.turnOffAI !== turnOffAI)
        throw new Error('Voice AI control readback mismatch')
      client.setQueryData(key, result)
      notify(
        'success',
        turnOffAI
          ? 'Voice AI turned off. Texting is unaffected.'
          : 'Voice AI turned on'
      )
    } catch {
      notify(
        'error',
        'Voice AI control could not be saved or verified. Refresh and review before trying again.'
      )
      void client.invalidateQueries({ queryKey: key, exact: true })
    } finally {
      setSaving(false)
    }
  }

  const aiEnabled = data ? !data.turnOffAI : undefined
  return (
    <section
      className={styles.policy}
      aria-label="Voice AI"
      data-on={aiEnabled || undefined}
    >
      <span className={styles.policyIcon} aria-hidden="true">
        <Bot size={17} />
      </span>
      <div className={styles.policyMain}>
        <div className={styles.policyTitle}>
          <h3>Voice AI</h3>
          <span
            className={`${styles.badge} ${query.isError || aiEnabled === false ? styles.warningBadge : ''}`}
            role="status"
          >
            {query.isError
              ? 'Unavailable'
              : !data
                ? 'Loading'
                : data.turnOffAI
                  ? 'Off · Ava answers no calls'
                  : 'On · Ava answers calls'}
          </span>
        </div>
        <p
          className={styles.description}
          id={`ai-enabled-description-${tenantId}`}
        >
          Phone calls only. When off, Ava answers no new calls and calls are
          handled by text: callers hear that the front desk is unavailable, and
          Missed Call Follow-Up can text them to continue by text. Calls already
          with Ava finish normally. Texting, including two-way AI texting,
          reminders and staff alerts, keeps working.
        </p>
        <p className={styles.meta}>
          <span>Main Phone</span>
          <strong>
            {formatPhone(data?.mainPhone ?? null) ?? 'Not configured'}
          </strong>
        </p>
        {query.isError && (
          <p role="alert" className={styles.notice}>
            Voice AI control couldn’t load.{' '}
            <Button variant="ghost" onClick={() => void query.refetch()}>
              Refresh Voice AI control
            </Button>
          </p>
        )}
        {data && !data.voiceEnabled && (
          <p className={styles.notice}>Voice isn’t enabled for this clinic.</p>
        )}
        {data && !data.mainPhoneRoutable && (
          <p role="alert" className={styles.notice}>
            Configure a valid Main Phone before turning off Voice AI.
          </p>
        )}
      </div>
      <div className={styles.policyControl}>
        <span className={styles.switchSlot}>
          {data && (
            <Switch
              aria-label="Voice AI"
              aria-describedby={`ai-enabled-description-${tenantId}`}
              checked={!data.turnOffAI}
              disabled={
                saving ||
                query.isFetching ||
                !data.voiceEnabled ||
                // Turning Voice AI off still requires a routable Main Phone
                // (settings_ai_control), though no call is sent to it.
                (!data.mainPhoneRoutable && !data.turnOffAI)
              }
              onChange={(event) => void update(!event.currentTarget.checked)}
            />
          )}
        </span>
      </div>
    </section>
  )
}

/**
 * Front desk transfer is one system-wide policy, not a clinic setting: Ava
 * answers every call that reaches Flio, and during staffed hours (Working
 * Hours) transfers a caller who explicitly asks for the front desk to the
 * clinic's Main Phone (Clinic Information). Read-only; it shares the AI control
 * read for the Main Phone and never fetches on its own.
 */
function FrontDeskTransfer({
  tenantId,
  repository,
  aiOff,
}: Props & { aiOff: boolean }) {
  const query = useQuery({
    queryKey: ['settings', tenantId, 'voice-ai-control'],
    queryFn: () => repository.getVoiceAIControl(tenantId),
    enabled: false,
  })
  const data = query.isError ? undefined : query.data
  const routable = data?.mainPhoneRoutable === true
  return (
    <section className={styles.policy} aria-label="Front desk transfer">
      <span className={styles.policyIcon} aria-hidden="true">
        <PhoneForwarded size={17} />
      </span>
      <div className={styles.policyMain}>
        <div className={styles.policyTitle}>
          <h3>Front desk transfer</h3>
          <span
            className={`${styles.badge} ${data && !routable ? styles.warningBadge : ''}`}
            role="status"
          >
            {!data
              ? 'Main Phone loading'
              : aiOff
                ? 'Not used while Voice AI is off'
                : routable
                  ? 'Staffed hours · on request'
                  : 'Main Phone needed'}
          </span>
        </div>
        <p
          className={styles.description}
          id={`front-desk-transfer-description-${tenantId}`}
        >
          Ava answers every call that reaches Flio. During your staffed Working
          Hours, a caller who asks for the front desk can be transferred to your
          Main Phone. When a transfer isn’t possible or no one answers, Ava
          stays with the caller and can take a message or a callback request.
        </p>
        <p className={styles.meta}>
          <span>Transfers to</span>
          <strong>
            {formatPhone(data?.mainPhone ?? null) ?? 'Not configured'}
          </strong>
        </p>
        {data && !routable && (
          <p role="alert" className={styles.notice}>
            Add a valid Main Phone in Clinic Information so callers can reach
            the front desk.
          </p>
        )}
      </div>
    </section>
  )
}
